/* eslint-disable no-console */
import { getPartner } from './partner'
import { getUser } from './user'
const { version } = require('../../package.json')

// Store console logs in memory for support purposes
let consoleLogHistory = []
const MAX_LOG_ENTRIES = 100

// Override console methods to capture logs
const originalConsoleLog = console.log
const originalConsoleWarn = console.warn

const addToLogHistory = (level, args) => {
  const timestamp = new Date().toISOString()
  const message = args
    .map((arg) =>
      typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
    )
    .join(' ')

  consoleLogHistory.push({
    timestamp,
    level,
    message: message.substring(0, 1000) // Limit message length
  })

  // Keep only the last MAX_LOG_ENTRIES
  if (consoleLogHistory.length > MAX_LOG_ENTRIES) {
    consoleLogHistory = consoleLogHistory.slice(-MAX_LOG_ENTRIES)
  }
}

// Override console methods
console.log = (...args) => {
  originalConsoleLog.apply(console, args)
  addToLogHistory('log', args)
}

console.warn = (...args) => {
  originalConsoleWarn.apply(console, args)
  addToLogHistory('warn', args)
}

// Hook into console.error - note that it might already be overridden by Bugsnag
// We'll add our logging to any existing override
const currentConsoleError = console.error
console.error = (...args) => {
  // Call the current console.error (which might be Bugsnag's override)
  currentConsoleError.apply(console, args)
  // Add to our log history
  addToLogHistory('error', args)
}

// Also capture unhandled errors and promise rejections
window.addEventListener('error', (event) => {
  addToLogHistory('error', [
    `Unhandled Error: ${event.message}`,
    `File: ${event.filename}:${event.lineno}:${event.colno}`
  ])
})

window.addEventListener('unhandledrejection', (event) => {
  addToLogHistory('error', [`Unhandled Promise Rejection: ${event.reason}`])
})

/**
 * Collect recent browser logs and system information for support
 * @param {number} maxEntries - Maximum number of log entries to include
 * @returns {string} Formatted log information
 */
export const collectLogsForSupport = (maxEntries = 50) => {
  const user = getUser()
  const partner = getPartner()

  const systemInfo = {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    dashboardVersion: version,
    userId: user?.username || 'unknown',
    partnerId: partner?.id || 'unknown',
    partnerName: partner?.name || 'unknown',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    cookiesEnabled: navigator.cookieEnabled,
    onlineStatus: navigator.onLine
  }

  const recentLogs = consoleLogHistory.slice(-maxEntries)

  let logReport = '=== SYSTEM INFORMATION ===\n'
  Object.entries(systemInfo).forEach(([key, value]) => {
    logReport += `${key}: ${value}\n`
  })

  logReport += '\n=== RECENT BROWSER LOGS ===\n'
  if (recentLogs.length === 0) {
    logReport += 'No recent logs available\n'
  } else {
    recentLogs.forEach((log) => {
      logReport += `[${log.timestamp}] ${log.level.toUpperCase()}: ${log.message}\n`
    })
  }

  logReport += '\n=== LOCAL STORAGE INFO ===\n'
  try {
    const storageKeys = Object.keys(localStorage)
    logReport += `Local Storage Keys (${storageKeys.length}): ${storageKeys.join(', ')}\n`
  } catch (e) {
    logReport += 'Could not access localStorage\n'
  }

  return logReport
}

/**
 * Create a mailto link with logs included
 * @param {string} subject - Email subject
 * @param {string} body - Additional email body content
 * @returns {string} Mailto URL
 */
export const createSupportEmailWithLogs = (
  subject = 'Dashboard Support Request',
  body = ''
) => {
  const logs = collectLogsForSupport()
  const emailBody = `${body}\n\n--- DEBUG INFORMATION ---\n${logs}`

  const params = new URLSearchParams({
    subject,
    body: emailBody
  })

  return `mailto:help@aivy.app?${params.toString()}`
}

/**
 * Get recent error logs from console history
 * @param {number} maxEntries - Maximum number of error entries
 * @returns {Array} Array of error log entries
 */
export const getRecentErrors = (maxEntries = 10) => {
  return consoleLogHistory
    .filter((log) => log.level === 'error' || log.level === 'warn')
    .slice(-maxEntries)
}

/**
 * Clear the console log history
 */
export const clearLogHistory = () => {
  consoleLogHistory = []
}
